import { useState } from 'react';
import { ClassificationBadge } from './ClassificationBadge.jsx';

const TYPE_LABEL = {
  email_open:     '📧 Email open',
  link_click:     '🔗 Link click',
  pdf_open:       '📄 PDF open',
  svg_open:       '🖼 SVG open',
  view_open:      '👁 View open',
  view_heartbeat: '⏱ View heartbeat',
  view_scroll:    '📜 View scroll',
  view_click:     '🖱 View click',
  view_copy:      '📋 View copy',
  view_blur:      '😶‍🌫️ View blur',
  view_focus:     '🎯 View focus',
  view_print:     '🖨 View print',
};

const POSITION_LABEL = { top: '⬆ top', mid: '↔ middle', bottom: '⬇ bottom', arrival: '· arrival', inner: '· inner' };

export function EventCard({ event }) {
  const [open, setOpen] = useState(false);
  const when = toDate(event.occurred_at);

  return (
    <div className="rounded border border-border bg-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <div>
          <div className="text-sm font-medium flex items-center gap-2">
            {TYPE_LABEL[event.type] || event.type}
            {event.position && (
              <span className="text-[10px] rounded bg-zinc-800 px-1.5 py-0.5 text-zinc-400">
                {POSITION_LABEL[event.position] || event.position}
              </span>
            )}
            {event.is_proxy_or_vpn && (
              <span className="text-[10px] rounded bg-purple-900/40 text-purple-300 px-1.5 py-0.5">VPN/proxy</span>
            )}
            {event.is_mobile_network && (
              <span className="text-[10px] rounded bg-sky-900/40 text-sky-300 px-1.5 py-0.5">mobile</span>
            )}
            {event.email_client && event.email_client !== 'unknown' && event.email_client !== 'other' && (
              <span className="text-[10px] rounded bg-emerald-900/40 text-emerald-300 px-1.5 py-0.5">{event.email_client}</span>
            )}
            {event.color_scheme && (
              <span className={`text-[10px] rounded px-1.5 py-0.5 ${event.color_scheme === 'dark' ? 'bg-indigo-900/50 text-indigo-200' : 'bg-amber-900/40 text-amber-200'}`}>
                {event.color_scheme === 'dark' ? '🌙' : '☀'} {event.color_scheme}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-400">
            <span>{when ? when.toLocaleString() : '—'}</span>
            {event.ip && <span className="font-mono">{event.ip}</span>}
            {event.country && <span>{event.city ? `${event.city}, ` : ''}{event.region ? `${event.region}, ` : ''}{event.country}</span>}
            {event.device_type && <span>{event.device_type} · {event.os}</span>}
            {event.browser && <span>{event.browser}</span>}
            {event.isp && <span className="opacity-70">{event.isp}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ClassificationBadge classification={event.classification} confidence={event.confidence} />
          <span className="text-xs text-zinc-600">{open ? '▲' : '▼'}</span>
        </div>
      </button>

      {open && <Details event={event} />}
    </div>
  );
}

function Details({ event }) {
  // Curated rows — the fields most useful for "is this real?" judgment.
  const curated = [
    ['Type',            event.type],
    ['Classification',  event.classification],
    ['Confidence',      event.confidence != null ? `${(event.confidence * 100).toFixed(0)}%` : null],
    ['UA bucket',       event.ua_bucket],
    ['UA reason',       event.ua_reason],
    ['UA is_real_open', bool(event.ua_is_real_open)],
    ['Open lifecycle',  event.open_lifecycle],
    ['IP',              event.ip],
    ['IP category',     event.ip_category],
    ['Country',         event.country_code ? `${event.country} (${event.country_code})` : event.country],
    ['Region · city',   [event.region, event.city].filter(Boolean).join(' · ')],
    ['Zip',             event.zip],
    ['Timezone',        event.timezone],
    ['Lat/Lon',         event.lat != null ? `${event.lat}, ${event.lon}` : null],
    ['ISP',             event.isp],
    ['Org',             event.org],
    ['ASN',             event.asn],
    ['AS name',         event.asname],
    ['Mobile network',  bool(event.is_mobile_network)],
    ['VPN / proxy',     bool(event.is_proxy_or_vpn)],
    ['Hosting / datacenter', bool(event.is_hosting)],
    ['Device',          [event.device_type, event.os, event.browser].filter(Boolean).join(' · ')],
    ['Email client',    event.email_client],
    ['Color scheme',    event.color_scheme],
    ['Accept-Language', event.accept_language],
    ['Referer',         event.referer],
    ['DNT',             event.dnt],
    ['Protocol',        event.protocol],
    ['Sec-CH-UA',       event.ch_ua],
    ['Platform',        event.ch_ua_platform],
    ['CH mobile',       event.ch_ua_mobile],
    ['CH model',        event.ch_ua_model],
    ['Fingerprint',     event.fingerprint],
    ['User-Agent',      event.user_agent],
    ['Position',        event.position],
    ['Recipient view ID', event.recipient_view_id],
    ['Owner UID',       event.owner_uid],
    ['Campaign ID',     event.campaign_id],
    ['Contact ID',      event.contact_id],
    ['Ref ID',          event.ref_id],
    ['Dwell ms',        event.dwell_ms],
    ['Scroll %',        event.scroll_pct],
    ['A/B test ID',     event.ab_test_id],
    ['Variant',         event.variant],
  ].filter(([, v]) => v != null && v !== '');

  // Everything else — schema-free overflow so the operator sees ALL fields,
  // including ones the curated list doesn't know about.
  const curatedKeys = new Set([
    'type','classification','confidence','ua_bucket','ua_reason','ua_is_real_open',
    'open_lifecycle','ip','ip_category','country','country_code','region','city','zip',
    'timezone','lat','lon','isp','org','asn','asname','is_mobile_network','is_proxy_or_vpn',
    'is_hosting','device_type','os','browser','email_client','color_scheme','accept_language',
    'referer','dnt','protocol','ch_ua','ch_ua_platform','ch_ua_mobile','ch_ua_model',
    'fingerprint','user_agent','position','recipient_view_id','owner_uid','campaign_id',
    'contact_id','ref_id','dwell_ms','scroll_pct','ab_test_id','variant',
    'id','occurred_at','created_at',                              // shown elsewhere
  ]);
  const overflow = Object.keys(event)
    .filter((k) => !curatedKeys.has(k))
    .map((k) => [k, event[k]])
    .filter(([, v]) => v != null && v !== '');

  const [showRaw, setShowRaw] = useState(false);

  return (
    <div className="border-t border-border bg-zinc-950/50 p-3 grid gap-3 text-xs">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
        {curated.map(([k, v]) => (
          <div key={k} className="flex gap-3">
            <span className="w-32 shrink-0 text-zinc-500">{k}</span>
            <span className="font-mono break-all text-zinc-200">{String(v)}</span>
          </div>
        ))}
      </div>

      {overflow.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            Other fields ({overflow.length})
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            {overflow.map(([k, v]) => (
              <div key={k} className="flex gap-3">
                <span className="w-32 shrink-0 text-zinc-500 break-all">{k}</span>
                <span className="font-mono break-all text-zinc-200">
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-700 rounded text-zinc-300 hover:text-zinc-100"
        >
          {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(JSON.stringify(event, replacer, 2))}
          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-zinc-700 rounded text-zinc-300 hover:text-zinc-100"
        >
          Copy JSON
        </button>
        <span className="text-[10px] text-zinc-500">
          {Object.keys(event).length} fields · doc id <span className="font-mono">{event.id}</span>
        </span>
      </div>

      {showRaw && (
        <pre className="bg-black/60 border border-zinc-800 rounded p-2 text-[11px] font-mono overflow-auto text-zinc-200 max-h-96">
{JSON.stringify(event, replacer, 2)}
        </pre>
      )}
    </div>
  );
}

// JSON.stringify replacer that coerces Firestore Timestamps into ISO strings
// so the raw view is legible.
function replacer(_k, v) {
  if (v && typeof v === 'object') {
    if (typeof v.toDate === 'function') return v.toDate().toISOString();
    if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString();
  }
  return v;
}

function bool(v) {
  if (v == null) return null;
  return v ? 'yes' : 'no';
}

function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}
